import { QRCodeSVG } from "qrcode.react";
import { type FormEvent, useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { APIError, request } from "../api";
import { useAuth } from "../auth";
import { JSONPreview } from "../components";
import type { User } from "../types";

export function LoginPage() {
  const { session, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (session) {
    return <Navigate to="/" replace />;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await login({
        email,
        password,
        mfa_code: mfaRequired ? mfaCode : undefined,
      });
      if (response.mfa_required) {
        setMfaRequired(true);
        return;
      }
      navigate("/", { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-layout">
      <section className="auth-context">
        <div className="auth-context__brand">NEXUS / CONTROL ROOM</div>
        <div className="auth-context__rail" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
        </div>
        <h1>Follow every handoff.</h1>
        <p>
          Inspect tenant integrations, trace provider traffic, and control the engine
          from one private operational surface.
        </p>
        <dl className="auth-context__facts">
          <div><dt>Access</dt><dd>Internal operators</dd></div>
          <div><dt>Scope</dt><dd>All Nexus tenants</dd></div>
          <div><dt>Controls</dt><dd>Audited by default</dd></div>
        </dl>
      </section>
      <section className="auth-card">
        <div>
          <p className="eyebrow">Operator access</p>
          <h2>{mfaRequired ? "Verify your second factor" : "Sign in to Nexus"}</h2>
          <p className="auth-card__intro">
            {mfaRequired
              ? "Enter the current code from your authenticator or a recovery code."
              : "Use your Nexus operator account. Tenant credentials never appear here."}
          </p>
        </div>
        <form className="stack-form" onSubmit={(event) => void submit(event)}>
          <label>
            Email
            <input
              type="email"
              autoComplete="username"
              required
              disabled={mfaRequired}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label>
            Password
            <input
              type="password"
              autoComplete="current-password"
              required
              disabled={mfaRequired}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          {mfaRequired && (
            <label>
              Authenticator or recovery code
              <input
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={mfaCode}
                onChange={(event) => setMfaCode(event.target.value)}
              />
            </label>
          )}
          {error && <div className="inline-error" role="alert">{error}</div>}
          <button type="submit" className="button button--primary button--wide" disabled={busy}>
            {busy ? "Verifying…" : mfaRequired ? "Verify and continue" : "Continue"}
          </button>
          {mfaRequired && (
            <button
              type="button"
              className="button button--text"
              onClick={() => {
                setMfaRequired(false);
                setMfaCode("");
              }}
            >
              Use another account
            </button>
          )}
        </form>
      </section>
    </main>
  );
}

interface SetupChallenge {
  user: User;
  mfa_required: boolean;
  totp_secret?: string;
  totp_uri?: string;
}

interface SetupResult {
  user: User;
  recovery_codes: string[];
}

export function SetupPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState<SetupChallenge>();
  const [result, setResult] = useState<SetupResult>();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This setup link is missing its one-time token.");
      return;
    }
    void request<SetupChallenge>("/auth/setup/begin", {
      method: "POST",
      body: JSON.stringify({ token }),
    }).then(setChallenge).catch((caught: APIError) => setError(caught.message));
  }, [token]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      setResult(await request<SetupResult>("/auth/setup/complete", {
        method: "POST",
        body: JSON.stringify({
          token,
          password,
          totp_code: totpCode,
        }),
      }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Account setup failed.");
    } finally {
      setBusy(false);
    }
  }

  if (result) {
    return (
      <main className="auth-layout auth-layout--setup">
        <section className="auth-card auth-card--recovery">
          <p className="eyebrow">Account active</p>
          <h1>Store these recovery codes now.</h1>
          <p>Each code works once. Nexus cannot display them again.</p>
          {result.recovery_codes.length > 0 && <JSONPreview value={result.recovery_codes} />}
          <button type="button" className="button button--primary" onClick={() => navigate("/login")}>
            Continue to sign in
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-layout auth-layout--setup">
      <section className="auth-card auth-card--setup">
        <p className="eyebrow">One-time account setup</p>
        <h1>{challenge ? `Welcome, ${challenge.user.display_name}.` : "Preparing your account…"}</h1>
        {challenge?.mfa_required && challenge.totp_uri && (
          <div className="totp-enrollment">
            <QRCodeSVG value={challenge.totp_uri} size={156} />
            <div>
              <h2>Connect an authenticator</h2>
              <p>Scan the code, then enter the six-digit code below.</p>
              <code>{challenge.totp_secret}</code>
            </div>
          </div>
        )}
        {challenge && (
          <form className="stack-form" onSubmit={(event) => void submit(event)}>
            <div className="form-grid">
              <label>
                New password
                <input
                  type="password"
                  minLength={14}
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </label>
              <label>
                Confirm password
                <input
                  type="password"
                  minLength={14}
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                />
              </label>
            </div>
            {challenge.mfa_required && (
              <label>
                Authenticator code
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={totpCode}
                  onChange={(event) => setTotpCode(event.target.value)}
                />
              </label>
            )}
            {error && <div className="inline-error" role="alert">{error}</div>}
            <button type="submit" className="button button--primary" disabled={busy}>
              {busy ? "Activating…" : "Activate account"}
            </button>
          </form>
        )}
        {!challenge && error && <div className="inline-error" role="alert">{error}</div>}
      </section>
    </main>
  );
}

